/**
 * <featured-collection-slider> — drag-to-scroll horizontal product slider.
 *
 * - Desktop (mouse): click + drag to scroll. Tiny click-vs-drag threshold so
 *   links inside the cards still work.
 * - Touch / trackpad: native horizontal scroll, untouched.
 * - Native browser scrollbar is left as-is; this script does not render one.
 */
class FeaturedCollectionSlider extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('[data-fcs-list]');
    if (!this.list) return;

    this.isDown = false;
    this.startX = 0;
    this.startScrollLeft = 0;
    this.dragMoved = false;
    this.DRAG_THRESHOLD = 6; // px before we treat as drag (so clicks still work)

    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onScroll = this.onScroll.bind(this);

    this.list.addEventListener('pointerdown', this.onPointerDown);
    // Suppress native image drag inside cards.
    this.list.addEventListener('dragstart', (e) => e.preventDefault());

    // Reveal the scrollbar while actively scrolling, then fade out after a moment.
    this.list.addEventListener('scroll', this.onScroll, { passive: true });
  }

  disconnectedCallback() {
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
    if (this.scrollTimer) clearTimeout(this.scrollTimer);
  }

  onScroll() {
    this.list.classList.add('is-scrolling');
    if (this.scrollTimer) clearTimeout(this.scrollTimer);
    this.scrollTimer = setTimeout(() => {
      this.list.classList.remove('is-scrolling');
    }, 700);
  }

  onPointerDown(event) {
    // Only intercept mouse — touch and pen keep native scroll behavior.
    if (event.pointerType !== 'mouse') return;
    if (event.target.closest('button, input, textarea, select')) return;

    this.isDown = true;
    this.dragMoved = false;
    this.startX = event.clientX;
    this.startScrollLeft = this.list.scrollLeft;

    window.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('pointerup', this.onPointerUp, { once: true });
  }

  onPointerMove(event) {
    if (!this.isDown) return;
    const dx = event.clientX - this.startX;

    if (!this.dragMoved && Math.abs(dx) >= this.DRAG_THRESHOLD) {
      this.dragMoved = true;
      this.list.classList.add('is-dragging');
      try {
        this.list.setPointerCapture(event.pointerId);
      } catch (e) {
        /* noop */
      }
    }

    if (this.dragMoved) {
      event.preventDefault();
      this.list.scrollLeft = this.startScrollLeft - dx;
    }
  }

  onPointerUp() {
    this.isDown = false;
    window.removeEventListener('pointermove', this.onPointerMove);

    if (this.dragMoved) {
      // Swallow the upcoming click so dragging doesn't open a product page.
      const swallow = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.list.removeEventListener('click', swallow, true);
      };
      this.list.addEventListener('click', swallow, true);
      setTimeout(() => this.list.removeEventListener('click', swallow, true), 50);

      this.list.classList.remove('is-dragging');
    }
    this.dragMoved = false;
  }
}

if (!customElements.get('featured-collection-slider')) {
  customElements.define('featured-collection-slider', FeaturedCollectionSlider);
}
